import React, { useState, useCallback } from 'react';
import {
    Card, Table, Input, Select, Button, Space, Tag, Typography,
    message, Modal, Tooltip, Popconfirm
} from 'antd';
import { ReloadOutlined, SendOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { customInstance } from '@/shared/api/mutator/custom-instance';

const { Title } = Typography;
const { Option } = Select;

interface RepaymentTracking {
    id: number;
    loanNumber: string;
    applicationNumber: string;
    essPaymentReference: string;
    scalefiTransactionNumber: string;
    checkNumber: string;
    deductionCode: string;
    paymentAmount: number;
    loanBalance: number;
    paymentDate: string;
    maturityDate: string;
    paymentMethod: string;
    reconciliationStatus: string;
    notificationStatus: string;
    notificationRetries: number;
    notificationError: string;
    notifiedAt: string;
    sourceType: string;
    branch: string;
    createdAt: string;
    updatedAt: string;
}

interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

const normalizePageResponse = <T,>(response: any): PageResponse<T> => {
    const payload = response?.data ?? response ?? {};
    const page = payload?.page ?? {};
    const content = Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload?._embedded?.content)
            ? payload._embedded.content
            : [];

    return {
        content,
        totalElements: payload?.totalElements ?? page?.totalElements ?? content.length,
        totalPages: payload?.totalPages ?? page?.totalPages ?? 0,
        size: payload?.size ?? page?.size ?? content.length,
        number: payload?.number ?? page?.number ?? 0,
    };
};

const statusColorMap: Record<string, string> = {
    PENDING: 'orange',
    SENT: 'green',
    FAILED: 'red',
    RECONCILED: 'green',
    MANUAL: 'blue',
    ESS_DEDUCTION: 'purple',
};

const EssRepaymentNotificationsView: React.FC = () => {
    const [data, setData] = useState<RepaymentTracking[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [resendingId, setResendingId] = useState<number | null>(null);
    const [detailModal, setDetailModal] = useState<{ open: boolean; record: RepaymentTracking | null }>({
        open: false,
        record: null,
    });
    const [filters, setFilters] = useState({
        query: '',
        notificationStatus: undefined as string | undefined,
    });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

    const fetchData = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const params: Record<string, any> = {
                page: page - 1,
                size: pageSize,
            };
            if (filters.query) params.query = filters.query;
            if (filters.notificationStatus) params.notificationStatus = filters.notificationStatus;

            const resp = await customInstance<PageResponse<RepaymentTracking>>({
                url: '/api/admin/ess-repayment-notifications',
                method: 'GET',
                params,
            });
            const pageData = normalizePageResponse<RepaymentTracking>(resp);
            setData(pageData.content || []);
            setTotal(pageData.totalElements || 0);
        } catch {
            message.error('Failed to load repayment notifications');
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.current, pagination.pageSize]);

    React.useEffect(() => {
        void fetchData();
    }, [fetchData]);

    const handleSearch = () => {
        setPagination({ ...pagination, current: 1 });
        void fetchData(1, pagination.pageSize);
    };

    const handleResend = async (id: number) => {
        setResendingId(id);
        try {
            const resp = await customInstance<{ notificationStatus?: string; message?: string }>({
                url: `/api/admin/ess-repayment-notifications/${id}/resend`,
                method: 'POST',
            });
            const status = resp?.notificationStatus;
            if (status === 'SENT') {
                message.success('Notification sent successfully');
            } else {
                message.warning('Resend attempted but status is: ' + status);
            }
            await fetchData();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to resend notification');
        } finally {
            setResendingId(null);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: 'Loan Number',
            dataIndex: 'loanNumber',
            key: 'loanNumber',
            width: 130,
        },
        {
            title: 'Application No.',
            dataIndex: 'applicationNumber',
            key: 'applicationNumber',
            width: 140,
            ellipsis: true,
        },
        {
            title: 'Check No.',
            dataIndex: 'checkNumber',
            key: 'checkNumber',
            width: 110,
        },
        {
            title: 'ESS Reference',
            dataIndex: 'essPaymentReference',
            key: 'essPaymentReference',
            width: 160,
            ellipsis: true,
        },
        {
            title: 'Amount',
            dataIndex: 'paymentAmount',
            key: 'paymentAmount',
            width: 120,
            render: (v: number) => v?.toLocaleString('en-TZ', { minimumFractionDigits: 2 }),
        },
        {
            title: 'Balance',
            dataIndex: 'loanBalance',
            key: 'loanBalance',
            width: 120,
            render: (v: number) => v != null ? v.toLocaleString('en-TZ', { minimumFractionDigits: 2 }) : '-',
        },
        {
            title: 'Payment Date',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            width: 120,
        },
        {
            title: 'Notification',
            dataIndex: 'notificationStatus',
            key: 'notificationStatus',
            width: 120,
            render: (s: string) => <Tag color={statusColorMap[s] || 'default'}>{s}</Tag>,
        },
        {
            title: 'Retries',
            dataIndex: 'notificationRetries',
            key: 'notificationRetries',
            width: 80,
        },
        {
            title: 'Source',
            dataIndex: 'sourceType',
            key: 'sourceType',
            width: 130,
            render: (s: string) => <Tag color={statusColorMap[s] || 'default'}>{s}</Tag>,
        },
        {
            title: 'Error',
            dataIndex: 'notificationError',
            key: 'notificationError',
            width: 160,
            ellipsis: true,
            render: (e: string) => e ? <Tooltip title={e}><Tag color="red">Error</Tag></Tooltip> : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right' as const,
            render: (_: any, record: RepaymentTracking) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => setDetailModal({ open: true, record })}
                        />
                    </Tooltip>
                    {(record.notificationStatus === 'FAILED' || record.notificationStatus === 'PENDING') && (
                        <Popconfirm
                            title="Resend notification to ESS?"
                            onConfirm={() => handleResend(record.id)}
                            okText="Send"
                            cancelText="Cancel"
                        >
                            <Tooltip title="Resend to ESS">
                                <Button
                                    type="link"
                                    icon={<SendOutlined />}
                                    size="small"
                                    loading={resendingId === record.id}
                                    danger={record.notificationStatus === 'FAILED'}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>ESS Repayment Notifications</Title>

            <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Space wrap>
                        <Input
                            placeholder="Search by loan no., application no., check no., ESS ref..."
                            prefix={<SearchOutlined />}
                            style={{ width: 380 }}
                            value={filters.query}
                            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                            onPressEnter={handleSearch}
                            allowClear
                        />

                        <Select
                            placeholder="Notification Status"
                            style={{ width: 180 }}
                            allowClear
                            value={filters.notificationStatus}
                            onChange={(value) => setFilters({ ...filters, notificationStatus: value })}
                        >
                            <Option value="PENDING">Pending</Option>
                            <Option value="SENT">Sent</Option>
                            <Option value="FAILED">Failed</Option>
                        </Select>

                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                            Search
                        </Button>

                        <Button icon={<ReloadOutlined />} onClick={() => fetchData()} loading={loading}>
                            Refresh
                        </Button>
                    </Space>

                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: 1600 }}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total,
                            showSizeChanger: true,
                            showTotal: (t) => `Total ${t} records`,
                            onChange: (page, pageSize) => {
                                setPagination({ current: page, pageSize });
                                void fetchData(page, pageSize);
                            },
                        }}
                        rowClassName={(record) =>
                            record.notificationStatus === 'FAILED' ? 'ant-table-row-danger' : ''
                        }
                    />
                </Space>
            </Card>

            <Modal
                title={`Repayment Tracking #${detailModal.record?.id}`}
                open={detailModal.open}
                onCancel={() => setDetailModal({ open: false, record: null })}
                footer={null}
                width={700}
            >
                {detailModal.record && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <tbody>
                            {([
                                ['Loan Number', detailModal.record.loanNumber],
                                ['Application Number', detailModal.record.applicationNumber],
                                ['Check Number', detailModal.record.checkNumber],
                                ['Deduction Code', detailModal.record.deductionCode],
                                ['ESS Payment Reference', detailModal.record.essPaymentReference],
                                ['ScaleFi Transaction No.', detailModal.record.scalefiTransactionNumber],
                                ['Payment Amount', detailModal.record.paymentAmount?.toLocaleString('en-TZ', { minimumFractionDigits: 2 })],
                                ['Loan Balance', detailModal.record.loanBalance?.toLocaleString('en-TZ', { minimumFractionDigits: 2 })],
                                ['Payment Date', detailModal.record.paymentDate],
                                ['Maturity Date', detailModal.record.maturityDate || '-'],
                                ['Payment Method', detailModal.record.paymentMethod],
                                ['Branch', detailModal.record.branch],
                                ['Source Type', detailModal.record.sourceType],
                                ['Reconciliation Status', detailModal.record.reconciliationStatus],
                                ['Notification Status', detailModal.record.notificationStatus],
                                ['Notification Retries', detailModal.record.notificationRetries],
                                ['Notified At', detailModal.record.notifiedAt ? dayjs(detailModal.record.notifiedAt).format('YYYY-MM-DD HH:mm:ss') : '-'],
                                ['Error', detailModal.record.notificationError || '-'],
                                ['Created At', dayjs(detailModal.record.createdAt).format('YYYY-MM-DD HH:mm:ss')],
                                ['Updated At', dayjs(detailModal.record.updatedAt).format('YYYY-MM-DD HH:mm:ss')],
                            ] as [string, any][]).map(([label, value]) => (
                                <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '6px 12px', fontWeight: 600, color: '#555', width: '40%' }}>{label}</td>
                                    <td style={{ padding: '6px 12px' }}>{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Modal>
        </div>
    );
};

export default EssRepaymentNotificationsView;
